import {
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { FC, useLayoutEffect, useState } from "react";
import CustomSwitch from "../../components/CustomSwitch";
import { useDispatch, useSelector } from "react-redux";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { NotificationManager } from "../../services/api/NotificationManager";
import {
  GetNotificationSettingsResponse,
} from "../../shared/payloads/notificationSettingsPayloads";
import DOMPurify from "dompurify";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import {
  BorderType,
} from "../../components/NotificationEditComponents/BorderSelect";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { useNotificationManager } from "../../hooks/useNotificationManager";
import { convertStringToLanguage } from "../../generic-utils";
import i18n from "../../i18n";
import { getCustomWebLogo } from "../../services/selectors/web-selectors";
import Tabs from "../../components/Tabs";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";

interface TrustPilotModalProps {
  createdNotificationId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  data?: GetNotificationSettingsResponse;
  branding?: any; // Add branding prop to match ActualVisitors pattern
}

type VariantType = "trustpilotReviews";

type TabType = "rules" | "design";

const notificationStyleId = v4();

const TrustPilotNotificationModal: FC<TrustPilotModalProps> = ({
  createdNotificationId,
  isOpen,
  onClose,
  onSaveSuccess,
  data,
  branding,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const language = convertStringToLanguage(i18n.language);

  const { createNotificationPrimitive } = useNotificationManager();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");

  const [minReviewStars, setMinReviewStars] = useState<number>(
    data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
  );
  const [minReviews, setMinReviews] = useState<number>(
    data?.minReviews !== undefined ? data?.minReviews : 1,
  );
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );

  const [selectedVariant, setSelectedVariant] = useState<VariantType>(
    data?.type ? (data?.type as VariantType) : "trustpilotReviews",
  );

  const [notificationHtml, setNotificationHtml] = useState<string>("");

  // Visual settings with branding fallbacks like in ActualVisitors
  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconBackgroundColor || null,
  );
  const [iconColor, setIconColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconColor || null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(
    data?.notificationVisualSettings?.roundness || null,
  );
  const [icon, setIcon] = useState<string | null>(
    data?.notificationVisualSettings?.icon || null,
  );

  const [backgroundOpacity, setBackgroundOpacity] = useState<number | null>(
    data?.notificationVisualSettings?.backgroundOpacity || null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    data?.notificationVisualSettings?.activeSize || "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(
    data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
      ? !!data?.notificationVisualSettings?.isShowingIcon
      : true,
  );
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(data?.notificationVisualSettings?.iconBackgroundRadius || null);

  const customWebBranding = useSelector(getCustomWebLogo);

  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  // Change detection logic similar to ActualVisitors
  useLayoutEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const minReviewStarsDefault =
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4;

    const minReviewsDefault =
      data?.minReviews !== undefined ? data?.minReviews : 1;

    const defaultIconBackgroundColor = data?.notificationVisualSettings?.iconBackgroundColor ?? branding?.iconBackgroundColor ?? null;
    const defaultIconColor = data?.notificationVisualSettings?.iconColor ?? branding?.iconColor ?? null;
    const defaultBorderRadius = data?.notificationVisualSettings?.roundness ?? branding?.borderRadius ?? null;
    const defaultBackgroundOpacity = data?.notificationVisualSettings?.backgroundOpacity ?? branding?.backgroundOpacity ?? null;
    const defaultIcon = data?.notificationVisualSettings?.icon ?? null;
    const defaultIconBackgroundRadius = data?.notificationVisualSettings?.iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? null;
    const defaultActive = data?.notificationVisualSettings?.activeSize ?? branding?.activeSize ?? "classic";
    const defaultShowIcon = data?.notificationVisualSettings?.isShowingIcon ?? branding?.brandingIsShowingIcon ?? true;

    const hasChanges = 
      (backgroundColor !== null && backgroundColor !== defaultBackgroundColor) ||
      (textColor !== null && textColor !== defaultTextColor) ||
      (minReviewStars !== minReviewStarsDefault) ||
      (minReviews !== minReviewsDefault) ||
      (withRepeat !== withRepeatDefault) ||
      (iconBackgroundColor !== null && iconBackgroundColor !== defaultIconBackgroundColor) ||
      (iconColor !== null && iconColor !== defaultIconColor) ||
      (borderRadius !== null && borderRadius !== defaultBorderRadius) ||
      (backgroundOpacity !== null && backgroundOpacity !== defaultBackgroundOpacity) ||
      (icon !== null && icon !== defaultIcon) ||
      (iconBackgroundRadius !== null && iconBackgroundRadius !== defaultIconBackgroundRadius) ||
      (active !== defaultActive) ||
      (showIcon !== defaultShowIcon);
      
    setDetectedChange(hasChanges);
  }, [backgroundColor, textColor, minReviewStars, minReviews, withRepeat, iconBackgroundColor, iconColor, borderRadius, backgroundOpacity, icon, iconBackgroundRadius, active, showIcon, data, branding]);

  const onCloseAndClearInputs = () => {
    setMinReviewStars(
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
    );
    setMinReviews(data?.minReviews !== undefined ? data?.minReviews : 1);
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
    setSelectedVariant("trustpilotReviews");
    onClose();
  };

  // Initialize visual settings with branding fallbacks
  useState(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpacity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : true,
    );
    setIcon(data?.notificationVisualSettings?.icon ?? null);
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
  });

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: selectedVariant,
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                iconColor ??
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        selectedVariant,
        undefined,
        undefined,
        undefined,
        language,
        undefined,
        undefined,
        t("campaign.notifications.trustPilot.previewText1") ?? "",
        minReviewStars ?? 4,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    active,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    branding?.activeSize,
    branding?.backgroundColor,
    branding?.backgroundOpacity,
    branding?.borderRadius,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
    branding?.iconColor,
    branding?.textColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.textColor,
    icon,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    minReviewStars,
    selectedVariant,
    showIcon,
    textColor,
    customWebBranding,
    t,
  ]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const onSave = async () => {
    try {
      setIsLoading(true);
      const _notificationManager = NotificationManager.getManager();
      let newCreatedNotificationId = undefined;
      await _notificationManager.deleteNotificationSettings(
        createdNotificationId,
      );
      newCreatedNotificationId = await createNotificationPrimitive(
        selectedVariant,
      );

      await _notificationManager.updateNotificationSettingsAsync({
        id: newCreatedNotificationId?.id
          ? newCreatedNotificationId?.id
          : createdNotificationId,
        isActive: true,
        repeatNotification: withRepeat,
        minReviews: minReviews,
        minReviewStars: minReviewStars,

        notificationVisualSettings: {
          backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
          textColor: textColor ?? branding?.textColor ?? "",
          iconBackgroundColor:
            iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
          iconColor: iconColor ?? branding?.iconColor ?? "",
          icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
          roundness: borderRadius ?? branding?.borderRadius ?? 0,
          linkTextColor: "",
          linkButtonBackgroundColor: "",
          backgroundOpacity:
            backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
          activeSize: active ?? branding?.activeSize ?? "classic",
          isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
          iconBackgroundRadius:
            iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
        },
      });
      setIsLoading(false);
      onSaveSuccess?.();
      onCloseAndClearInputs();
    } catch (e: any) {
      setIsLoading(false);
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpacity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setIconColor(null);
    setIcon(null);
    setMinReviews(data?.minReviews !== undefined ? data?.minReviews : 1);
    setMinReviewStars(
      data?.minReviewStars !== undefined ? data?.minReviewStars : 4,
    );
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
  };

  // Custom rules tab content for TrustPilot
  const TrustPilotRulesTabContent = () => (
    <div>
      <Typography
        style={{
          fontSize: 18,
          marginTop: 16,
          marginBottom: 16,
          fontWeight: "bold",
        }}
      >
        {t("campaign.notifications.settings.displayRules.show")}
      </Typography>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          flexWrap: "wrap",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: 104,
          }}
        >
          <Box
            style={{ marginBottom: 16, display: "flex", alignItems: "center" }}
          >
            <Typography
              style={{
                flex: 1,
                fontSize: 16,
              }}
            >
              {t(
                "campaign.notifications.settings.displayRules.trustPilot.minReviews",
              )}
            </Typography>
            <TextField
              style={{ width: 108, marginRight: 12 }}
              type='number'
              variant='outlined'
              value={minReviews}
              onChange={event => {
                const result = parseInt(event.target.value, 10);
                if (result <= 0) {
                  setMinReviews(1);
                } else {
                  setMinReviews(result);
                }
              }}
              InputProps={{
                inputProps: { min: 1 },
                style: { height: 44 },
              }}
            />
            <Box style={{ width: 72 }} />
          </Box>
          <Box style={{ display: "flex", alignItems: "center" }}>
            <Typography
              style={{
                flex: 1,
                fontSize: 16,
              }}
            >
              {t(
                "campaign.notifications.settings.displayRules.trustPilot.reviewsWithRating",
              )}
            </Typography>
            <TextField
              style={{ width: 108, marginRight: 12 }}
              type='number'
              variant='outlined'
              value={minReviewStars}
              onChange={event => {
                const result = parseInt(event.target.value, 10);
                if (result >= 5) {
                  setMinReviewStars(5);
                } else if (result <= 0) {
                  setMinReviewStars(1);
                } else {
                  setMinReviewStars(result);
                }
              }}
              InputProps={{
                inputProps: { min: 1, max: 5 },
                style: { height: 44 },
              }}
            />
            <Typography
              style={{
                fontSize: 16,
                width: 72,
              }}
            >
              {t(
                "campaign.notifications.settings.displayRules.trustPilot.stars",
              )}
            </Typography>
          </Box>
        </Box>
        <Box style={{ flex: 1 }}>
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              height: 52,
            }}
          >
            <Typography style={{ fontSize: 16 }}>
              {t(
                "campaign.notifications.settings.displayRules.repeatNotification",
              )}
            </Typography>
            <Box width='35%'>
              <CustomSwitch
                checked={withRepeat}
                onSwitch={() => setWithRepeat(!withRepeat)}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );

  const tabs = [
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: <TrustPilotRulesTabContent />,
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpacity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon={false}
          icon={icon}
          iconColor={iconColor}
          setIcon={setIcon}
          setIconColor={setIconColor}
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      title={t("campaign.notifications.trustPilot.title")}
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      isLoading={isLoading}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
    >
      <Tabs onTabChange={handleChange} selectedTab={selectedTab} tabs={tabs} />
    </NotificationModalBase>
  );
};

export default TrustPilotNotificationModal;
